// Pure helpers for the form-state save path (WS 4.1 substrate).
// Ported verbatim in semantics from the legacy
// `src/frontend/src/workspace/components/action/WorkspaceActionController.tsx`
// god-component. Kept side-effect-free so vitest can hit every branch
// without the React harness.
import type { FieldDef, Json, RecordData } from "../../core";
import { enumKey } from "../../managers/cell_utils";

/**
 * Auto-managed fields — never include in the save payload, never validate
 * as required, never count toward "dirty". Mirrors the 7 auto-fields every
 * `DomainModel` injects on the backend (see `kernel/model.py` →
 * `_build_auto_fields`).
 */
export const AUTO_FIELDS: ReadonlySet<string> = new Set([
    "record_uuid", "dbid",
    "created_at_utc", "updated_at_utc", "revision",
    "created_uid", "updated_uid",
]);

/**
 * Compose the save payload from the draft.
 *
 * - When `viewFields` is supplied, restrict the payload to fields declared
 *   in the form view DSL (avoids sending list-view-only columns that the
 *   user couldn't have edited).
 * - Skip every entry in `AUTO_FIELDS` and every field carrying
 *   `workflow=true` (foundation.workflow Phase 2 — the ORM write-guard
 *   would reject a direct update; transitions go through
 *   `env.workflow.transition()`).
 * - Skip fields whose draft value is `undefined` — they weren't loaded
 *   for this view; sending null would clobber the DB value.
 * - When `originalRecord` is supplied (UPDATE path), strip every field
 *   whose draft value matches the original (dirty filter). Skipped on
 *   CREATE so every declared field reaches the backend at insert time.
 * - For `reference` (M2O): extract `.id` from the `{id, display_name}`
 *   shape; raw UUID strings pass through.
 * - For `many2many`: command-tuple list (`[[6,0,[ids]]]` set, or
 *   buffered `[[0,0,v],[1,uuid,v]…]`) passes through verbatim;
 *   `[{id, display_name},…]` chip lists are mapped to a single SET tuple.
 * - For `one2many`: command tuples pass through; anything else is
 *   skipped (the inline-list editor buffers tuples lazily).
 * - Scalars: `val ?? null` — empty strings become null at the wire.
 */
export function buildSaveValues(
    draft: RecordData,
    fieldDefs: FieldDef[],
    viewFields: string[] | null,
    originalRecord?: RecordData,
): Record<string, Json> {
    const allowedFields = viewFields ? new Set(viewFields) : null;
    const values: Record<string, Json> = {};
    for (const fd of fieldDefs) {
        if (allowedFields && !allowedFields.has(fd.name)) continue;
        if (AUTO_FIELDS.has(fd.name)) continue;
        if (fd.workflow) continue;
        const val = draft[fd.name];
        if (val === undefined) continue;
        if (originalRecord !== undefined && isPristine(fd, val, originalRecord[fd.name])) continue;

        if (fd.type === "one2many") {
            if (Array.isArray(val) && val.length > 0 && Array.isArray(val[0])) {
                values[fd.name] = val as Json;
            }
            continue;
        }
        if (fd.type === "many2many") {
            if (Array.isArray(val) && val.length > 0 && Array.isArray(val[0])) {
                values[fd.name] = val as Json;
            } else {
                const items = (Array.isArray(val) ? val : []) as Array<{ id?: string }>;
                const ids = items.map((x) => x.id).filter((x): x is string => Boolean(x));
                values[fd.name] = [[6, 0, ids]] as Json;
            }
            continue;
        }
        if (fd.type === "reference" && val && typeof val === "object" && "id" in (val as object)) {
            const refId = (val as { id?: string }).id;
            values[fd.name] = (refId ?? null) as Json;
            continue;
        }
        if (fd.type === "enum") {
            // Rich read shape `[key, label]` → persist the key. A bare key (after
            // an inline edit) passes through enumKey unchanged.
            const key = enumKey(val);
            values[fd.name] = (key ?? null) as Json;
            continue;
        }
        values[fd.name] = (val ?? null) as Json;
    }
    return values;
}

/**
 * Field-aware equality check for dirty tracking. The wire shape varies
 * by field type, so naive deep-equal would over-report changes (e.g.
 * a Reference whose `display_name` rehydrated after a partial fetch).
 *
 * - Reference: compare on resolved UUID (`.id` or raw string).
 * - One2many / Many2many: JSON deep-equal (tuple lists are array-like).
 * - JSON / properties: JSON deep-equal.
 * - "no value" tri-equivalence: null / undefined / "" are interchangeable.
 * - Everything else: `===`.
 */
export function isPristine(fd: FieldDef, draftVal: unknown, originalVal: unknown): boolean {
    if (fd.type === "reference") {
        const draftId = draftVal && typeof draftVal === "object" && "id" in (draftVal as object)
            ? (draftVal as { id?: string }).id ?? null
            : (draftVal ?? null);
        const origId = originalVal && typeof originalVal === "object" && "id" in (originalVal as object)
            ? (originalVal as { id?: string }).id ?? null
            : (originalVal ?? null);
        return draftId === origId;
    }
    if (fd.type === "enum") {
        // Compare on the stored key so `[key, label]` (loaded) vs a bare key
        // (after edit) doesn't read as a change when the key is unchanged.
        const d = enumKey(draftVal) ?? null;
        const o = enumKey(originalVal) ?? null;
        return d === o;
    }
    if (fd.type === "one2many" || fd.type === "many2many") {
        return JSON.stringify(draftVal) === JSON.stringify(originalVal);
    }
    if (fd.type === "json" || fd.name === "properties") {
        return JSON.stringify(draftVal ?? null) === JSON.stringify(originalVal ?? null);
    }
    const draftEmpty = draftVal === null || draftVal === undefined || draftVal === "";
    const origEmpty = originalVal === null || originalVal === undefined || originalVal === "";
    if (draftEmpty && origEmpty) return true;
    return draftVal === originalVal;
}

/** A required field the draft hasn't filled — both the machine `name`
    (for inline red-border highlighting) and the human `label` (for the
    toast summary). */
export interface MissingRequiredField {
    name: string;
    label: string;
}

/**
 * Returns the required fields with no value in the draft, carrying BOTH
 * the field name (drives inline red-border / red-label highlighting) and
 * the label (drives the summary toast). Auto-managed and workflow-
 * controlled fields are exempt. Booleans are exempt because `false` is a
 * valid value (a required-but-unchecked checkbox is still "filled").
 */
export function collectRequiredMissing(
    draft: RecordData,
    fieldDefs: FieldDef[],
    invisibleFieldNames?: ReadonlySet<string>,
    viewRequiredNames?: ReadonlySet<string>,
): MissingRequiredField[] {
    return fieldDefs
        // A field is required when the MODEL marks it required OR the form-view
        // layout marks it `required="1"` (matching the `*` in FormSheet). The
        // latter lets a wizard require a field whose model column is nullable
        // (e.g. a write-only password popped before persist).
        .filter((fd) => (fd.required === true || viewRequiredNames?.has(fd.name) === true) && fd.type !== "boolean")
        .filter((fd) => !AUTO_FIELDS.has(fd.name))
        .filter((fd) => !fd.workflow)
        // Skip fields hidden by the form view's `invisible="..."` predicate
        // (resolved against the current draft by the caller). These fields
        // either have backend-managed defaults (back-references the ORM
        // auto-injects through nested-tuple creates) or are part of an
        // alternate-state branch the user can't fill in — blocking save on
        // them creates "you can't save and there's nothing visible to fix"
        // dead ends. Visible required fields are still enforced.
        .filter((fd) => !invisibleFieldNames?.has(fd.name))
        .filter((fd) => {
            const v = draft[fd.name];
            return v === null || v === undefined || v === "";
        })
        .map((fd) => ({ name: fd.name, label: fd.label ?? fd.name }));
}

/**
 * Returns the labels of required fields with no value in the draft —
 * a thin label-only projection of {@link collectRequiredMissing}.
 *
 * The caller surfaces the returned list as a toast / inline error before
 * dispatching the save.
 */
export function validateRequired(
    draft: RecordData,
    fieldDefs: FieldDef[],
    invisibleFieldNames?: ReadonlySet<string>,
    viewRequiredNames?: ReadonlySet<string>,
): string[] {
    return collectRequiredMissing(draft, fieldDefs, invisibleFieldNames, viewRequiredNames).map((m) => m.label);
}

/**
 * True when the draft and the original differ on any non-auto field.
 * `isDirty(draft, original)` matches the legacy `useMemo` semantics —
 * union of keys, AUTO_FIELDS exempt, JSON deep-equal per key.
 */
export function isDirty(draft: RecordData, original: RecordData): boolean {
    const allKeys = new Set<string>([...Object.keys(draft), ...Object.keys(original)]);
    for (const k of allKeys) {
        if (AUTO_FIELDS.has(k)) continue;
        if (JSON.stringify(draft[k]) !== JSON.stringify(original[k])) return true;
    }
    return false;
}
